// src/pages/customer/RegisterCustomer.js
// Página para registrar un cliente con una persona asociada

import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { createCustomer } from '../../services/customerApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const RegisterCustomer = () => {
  const { user } = useAuth();
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    // Datos del cliente
    email: '',
    phone_whatsapp: '',
    phone_2: '',
    timezone: 'America/Bogota',
    marketing_opt_in: 0,
    // Datos de la persona
    name: '',
    surname: '',
    lastname: '',
    second_surname: '',
    document: '',
    id_document_type: 1,
    id_person_type: 1
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  // Opciones para los selects
  const documentTypes = [
    { id: 1, name: 'Cédula de Ciudadanía' },
    { id: 2, name: 'Cédula de Extranjería' },
    { id: 3, name: 'Pasaporte' },
    { id: 4, name: 'Tarjeta de Identidad' }
  ];

  const personTypes = [
    { id: 1, name: 'Persona Natural' },
    { id: 2, name: 'Persona Jurídica' }
  ];

  const timezones = [
    { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
    { value: 'America/Mexico_City', label: 'México' },
    { value: 'America/New_York', label: 'Estados Unidos (Este)' },
    { value: 'America/Los_Angeles', label: 'Estados Unidos (Oeste)' }
  ];

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // Validar formulario
  const validateForm = () => {
    if (!formData.email) return 'El email es obligatorio';
    if (!formData.phone_whatsapp) return 'El teléfono WhatsApp es obligatorio';
    if (!formData.name) return 'El nombre es obligatorio';
    if (!formData.surname) return 'El apellido es obligatorio';
    if (!formData.document) return 'El documento es obligatorio';
    
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Formato de email inválido';
    
    // Validar formato de teléfono (solo números)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone_whatsapp)) return 'El teléfono debe tener 10 dígitos';
    
    return null;
  };

  // Construir payload para el backend
  const buildPayload = () => {
    const payload = {
      email: formData.email,
      phone_whatsapp: formData.phone_whatsapp,
      timezone: formData.timezone,
      marketing_opt_in: formData.marketing_opt_in,
      person: {
        name: formData.name,
        surname: formData.surname,
        lastname: formData.lastname,
        second_surname: formData.second_surname,
        document: formData.document,
        id_document_type: parseInt(formData.id_document_type),
        id_person_type: parseInt(formData.id_person_type)
      }
    };
    
    // Agregar phone_2 si tiene valor
    if (formData.phone_2) {
      payload.phone_2 = formData.phone_2;
    }
    
    return payload;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (validation) {
      showAlert(validation, 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = buildPayload();
      console.log('Payload enviado:', payload);
      
      const { ok, status, data } = await createCustomer(payload);
      
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Cliente registrado exitosamente', 'success');
        // Limpiar formulario
        setFormData({
          email: '',
          phone_whatsapp: '',
          phone_2: '',
          timezone: 'America/Bogota',
          marketing_opt_in: 0,
          name: '',
          surname: '',
          lastname: '',
          second_surname: '',
          document: '',
          id_document_type: 1,
          id_person_type: 1
        });
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error registrando el cliente';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showAlert('Error inesperado al registrar el cliente', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header>
                <h4 className="mb-0">Registrar Nuevo Cliente</h4>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  {/* Información del Cliente */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Información del Cliente</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="ejemplo@correo.com"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Teléfono WhatsApp *</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone_whatsapp"
                              value={formData.phone_whatsapp}
                              onChange={handleChange}
                              placeholder="3001234567"
                              maxLength="10"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Teléfono 2 (Opcional)</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone_2"
                              value={formData.phone_2}
                              onChange={handleChange}
                              placeholder="3001234567"
                              maxLength="10"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Zona Horaria</Form.Label>
                            <Form.Select
                              name="timezone"
                              value={formData.timezone}
                              onChange={handleChange}
                            >
                              {timezones.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                  {tz.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="checkbox"
                              name="marketing_opt_in"
                              label="Acepta recibir comunicaciones de marketing"
                              checked={formData.marketing_opt_in === 1}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Información de la Persona */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Información Personal</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Juan"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Primer Apellido *</Form.Label>
                            <Form.Control
                              type="text"
                              name="surname"
                              value={formData.surname}
                              onChange={handleChange}
                              placeholder="Pérez"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Segundo Apellido</Form.Label>
                            <Form.Control
                              type="text"
                              name="lastname"
                              value={formData.lastname}
                              onChange={handleChange}
                              placeholder="Gómez"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Apellido Adicional</Form.Label>
                            <Form.Control
                              type="text"
                              name="second_surname"
                              value={formData.second_surname}
                              onChange={handleChange}
                              placeholder="López"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número de Documento *</Form.Label>
                            <Form.Control
                              type="text"
                              name="document"
                              value={formData.document}
                              onChange={handleChange}
                              placeholder="123456789"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Tipo de Documento</Form.Label>
                            <Form.Select
                              name="id_document_type"
                              value={formData.id_document_type}
                              onChange={handleChange}
                            >
                              {documentTypes.map(docType => (
                                <option key={docType.id} value={docType.id}>
                                  {docType.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Tipo de Persona</Form.Label>
                            <Form.Select
                              name="id_person_type"
                              value={formData.id_person_type}
                              onChange={handleChange}
                            >
                              {personTypes.map(personType => (
                                <option key={personType.id} value={personType.id}>
                                  {personType.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Registrando...
                        </>
                      ) : (
                        'Registrar Cliente'
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default RegisterCustomer;
